import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { ShieldCheck, Inbox, ToggleRight, Send, CreditCard, Search, Gift, Check, MapPin, Star } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1665249934445-1de680641f50?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhcGFydG1lbnQlMjBpbnRlcmlvciUyMHN1bmxpZ2h0fGVufDB8fHx8MTc4Mjg1MDIyMHww&ixlib=rb-4.1.0&q=85";

export const KEY_MOMENTS = [
  { url: "https://images.unsplash.com/photo-1780733064206-4f200e41daa8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHw0fHxoYXBweSUyMHBlcnNvbiUyMHJlY2VpdmluZyUyMGhvdXNlJTIwa2V5c3xlbnwwfHx8fDE3ODI5NTk3MzR8MA&ixlib=rb-4.1.0&q=85&w=500&h=500&fit=crop", alt: "Renter smiling with new keys" },
  { url: "https://images.unsplash.com/photo-1714647211818-319b3ec16f38?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHw0fHxzbWlsaW5nJTIwY291cGxlJTIwaG9sZGluZyUyMGtleXMlMjBuZXclMjBob21lJTIwZXhjaXRlZHxlbnwwfHx8fDE3ODI5NTk3NDR8MA&ixlib=rb-4.1.0&q=85&w=500&h=500&fit=crop", alt: "Excited couple in their new home" },
  { url: "https://images.unsplash.com/photo-1604186293649-8e96122e0474?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxzbWlsaW5nJTIwY291cGxlJTIwaG9sZGluZyUyMGtleXMlMjBuZXclMjBob21lJTIwZXhjaXRlZHxlbnwwfHx8fDE3ODI5NTk3NDR8MA&ixlib=rb-4.1.0&q=85&w=500&h=500&fit=crop", alt: "Happy couple moving in" },
  { url: "https://images.unsplash.com/photo-1758523671087-b256bbbca475?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwyfHxleGNpdGVkJTIwd29tYW4lMjBjZWxlYnJhdGluZyUyMG5ldyUyMGFwYXJ0bWVudHxlbnwwfHx8fDE3ODI5NTk3NDR8MA&ixlib=rb-4.1.0&q=85&w=500&h=500&fit=crop", alt: "Couple carrying boxes into new apartment" },
];

const steps = [
  { icon: ShieldCheck, title: "Get verified once", text: "Background, credit, income & ID checks — then earn your Verified badge." },
  { icon: Inbox, title: "Receive offers", text: "Landlords pitch you. Compare offers side-by-side in your inbox." },
  { icon: ToggleRight, title: "Toggle when done", text: "Found a place? Pause matchmaking. Reactivate anytime for $10." },
];

// Stable animation configs (avoid new object identities on every render)
const FADE_FROM = { opacity: 0, y: 16 };
const FADE_TO = { opacity: 1, y: 0 };
const FADE_UP = { opacity: 0, y: 24 };
const VIEWPORT_ONCE = { once: true };
const HERO_TXT_T = { duration: 0.6 };
const HERO_CARD_T = { duration: 0.6, delay: 0.15 };

export default function Landing() {
  const navigate = useNavigate();
  const [promo, setPromo] = useState(null);
  useEffect(() => { api.get("/promo/status").then(({ data }) => setPromo(data)).catch(() => {}); }, []);
  return (
    <div>
      {promo?.active && (
        <div data-testid="promo-banner" className="bg-emerald-600 text-white text-center text-sm py-2.5 px-4 font-semibold flex items-center justify-center gap-2">
          <Gift className="w-4 h-4" />
          First 100 tenants verify FREE — only {promo.spots_left} spots left!
          <button onClick={() => { localStorage.setItem("rm_pending_role", "tenant"); navigate("/auth?role=tenant"); }} className="underline underline-offset-2 ml-1">Claim yours</button>
        </div>
      )}
      <Navbar />
      {/* Hero */}
      <section className="relative overflow-hidden bg-secondary/30">
        <div className="absolute inset-0">
          <img src={HERO} alt="apartment" className="w-full h-full object-cover opacity-[0.07]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-8 py-16 md:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={FADE_FROM} animate={FADE_TO} transition={HERO_TXT_T}>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-primary bg-primary/10 rounded-full px-3 py-1.5 mb-5">
              <ShieldCheck className="w-3.5 h-3.5" /> Offer-based rental marketplace
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-bold text-foreground leading-[1.04]">
              Stop applying.<br />Start <span className="text-primary">receiving offers.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl">
              Verified tenants don't chase listings. Landlords pitch them. Get screened once, then sit back and compare real rental offers.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" data-testid="hero-tenant-cta" onClick={() => { localStorage.setItem("rm_pending_role", "tenant"); navigate("/auth?role=tenant"); }}>
                I'm a Tenant
              </Button>
              <Button size="lg" variant="outline" data-testid="hero-landlord-cta" onClick={() => { localStorage.setItem("rm_pending_role", "landlord"); navigate("/auth?role=landlord"); }}>
                I'm a Landlord
              </Button>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-emerald-700">
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4" /> Credit &amp; background</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4" /> Income verified</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4" /> ID confirmed</span>
            </div>
          </motion.div>

          {/* Product preview */}
          <motion.div
            initial={FADE_UP} animate={FADE_TO} transition={HERO_CARD_T}
            className="hidden lg:block relative pb-14"
          >
            {/* Screening card */}
            <div className="bg-white border border-border rounded-2xl shadow-lg p-6">
              <div className="flex items-center gap-3 pb-3 border-b border-border">
                <img src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?crop=entropy&cs=srgb&fm=jpg&w=120&h=120&fit=crop&q=85" alt="John Smith" className="w-10 h-10 rounded-full object-cover border border-border" />
                <div className="min-w-0">
                  <div className="font-display font-semibold text-sm leading-tight">John Smith</div>
                  <div className="text-[11px] text-muted-foreground flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-primary" /> Verified renter</div>
                </div>
                <span className="ml-auto text-[10px] uppercase tracking-wide text-emerald-700 font-bold bg-emerald-50 px-2 py-0.5 rounded-full">Online now</span>
              </div>
              <div className="text-xs font-semibold text-muted-foreground mt-4 mb-1">Screening Report</div>
              <div className="flex items-end gap-2">
                <span className="font-display text-4xl font-extrabold tracking-tighter text-slate-900 leading-none">716</span>
                <span className="mb-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Good score</span>
              </div>
              <div className="relative mt-3 h-2 rounded-full bg-gradient-to-r from-red-400 via-amber-400 to-emerald-500">
                <span className="absolute -top-1 w-4 h-4 rounded-full bg-white border-2 border-slate-800 shadow -translate-x-1/2" style={{ left: "76%" }} />
              </div>
              <div className="grid grid-cols-4 gap-2 mt-5">
                {[["Criminal", 0], ["Evictions", 0], ["Collections", 1], ["Records", 0]].map(([l, c]) => (
                  <div key={l} className="rounded-xl border border-border py-2.5 text-center">
                    <div className={`font-display text-lg font-bold ${c ? "text-red-600" : "text-slate-800"}`}>{c}</div>
                    <div className="text-[9px] uppercase tracking-wide text-muted-foreground">{l}</div>
                  </div>
                ))}
              </div>
            </div>
            {/* Floating offer chip */}
            <div className="absolute bottom-0 -left-6 bg-white border border-border rounded-2xl shadow-xl p-4 w-52">
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">New offer</span>
                <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
              </div>
              <div className="font-display text-xl font-extrabold tracking-tight text-slate-900 mt-1">$2,150<span className="text-xs font-medium text-muted-foreground">/mo</span></div>
              <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" /> Sunny 2BR · Austin, TX</div>
            </div>
          </motion.div>
        </div>
      </section>


      {/* How it works */}
      <section className="max-w-7xl mx-auto px-6 md:px-8 py-20">
        <p className="overline text-muted-foreground">How it works</p>
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-semibold mt-2 mb-12">Tenants don't apply. Landlords pitch them.</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <motion.div key={s.title} initial={FADE_FROM} whileInView={FADE_TO} viewport={VIEWPORT_ONCE} transition={{ delay: i * 0.1 }}
              className="bg-white border border-border rounded-sm p-8 hover:shadow-md transition-shadow">
              <div className="w-11 h-11 rounded-sm bg-primary/10 text-primary grid place-items-center mb-5">
                <s.icon className="w-5 h-5" />
              </div>
              <h3 className="font-display text-xl font-semibold mb-2">{s.title}</h3>
              <p className="text-muted-foreground">{s.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Key moments strip */}
      <section className="max-w-7xl mx-auto px-6 md:px-8 pb-4">
        <div className="flex items-end justify-between mb-6">
          <div>
            <p className="overline text-primary">Move-in day</p>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-semibold mt-2">Real renters. Real keys.</h2>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {KEY_MOMENTS.map((m, i) => (
            <motion.div key={m.url} initial={FADE_FROM} whileInView={FADE_TO} viewport={VIEWPORT_ONCE} transition={{ delay: i * 0.08 }}
              className="relative overflow-hidden rounded-sm border border-border aspect-square group">
              <img src={m.url} alt={m.alt} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
            </motion.div>
          ))}
        </div>
      </section>

      {/* Landlord band */}
      <section className="bg-foreground text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-20 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="overline text-white/60">For landlords</p>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-semibold mt-2 mb-4">Pre-screened tenants, ready for your offer.</h2>
            <p className="text-white/70 mb-6">Browse verified, active tenant profiles or post a property and let our engine match you with fitting renters. Build offers with incentives and close the deal in-app.</p>
            <Button size="lg" variant="secondary" data-testid="landlord-band-cta" onClick={() => { localStorage.setItem("rm_pending_role", "landlord"); navigate("/auth?role=landlord"); }}>
              Start sending offers
            </Button>
          </div>
          <div className="grid grid-cols-3 gap-4">
            {[Search, Send, CreditCard].map((Icon, i) => (
              <div key={["search", "send", "card"][i]} className="bg-white/5 border border-white/10 rounded-sm p-6 grid place-items-center">
                <Icon className="w-7 h-7 text-white/80" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="max-w-7xl mx-auto px-6 md:px-8 py-10 text-sm text-muted-foreground">
        <img src="/brand-logo.png" alt="MyRentalMatch" className="h-8 w-auto mb-3" />
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-3">
          <a href="/privacy" data-testid="footer-privacy" className="hover:text-primary transition-colors">Privacy Policy</a>
          <a href="/support" data-testid="footer-support" className="hover:text-primary transition-colors">Support</a>
          <a href="/tenant-pricing" className="hover:text-primary transition-colors">Pricing</a>
        </div>
        © {new Date().getFullYear()} MyRentalMatch · Offer-based rentals
      </footer>
    </div>
  );
}
